import { dayjs } from 'element-plus'
import numeral from 'numeral'
import type { HttpResponse } from '@/utils/http/request'

const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'
const DATE_FORMAT = 'YYYY-MM-DD'
type ILabelFormatType = 'period' | 'month'

export function useFormat() {
  /**
   * @description: 金额格式化千分位保留两位小数
   * @param {number} val
   * @return {string}
   */
  const formatAmountOfPlace = (val: number): string => {
    return val ? numeral(val).format('0,0.00') : '0.00'
  }

  /**
   * @description: 千分位格式化金额
   * @param {string} val
   * @return {number}
   */
  const formatPlaceOfAmount = (val: string | number): number => {
    if (val) {
      const str = numeral(val).format('0,0.00')
      return numeral(str).value() as number
    }
    return 0
  }

  /**
   * @description: 银行账号格式化 例：2222 2222 2222 222
   * @param {string} val
   * @return {string}
   */
  const formatBankAccount = (val: string): string => {
    let account = ''
    for (let i = 0; i < val.length; i += 1) {
      if (i % 4 === 0) {
        account += `${val.substring(i, i + 4)} `
      }
    }
    return account
  }

  /**
   * @description: 格式化form验证
   * @param {string} message
   * @param {*} trigger
   * @return {*}
   */
  const formatFormRole = (message: string, trigger = 'change') => {
    return [{ required: true, message, trigger }]
  }

  /**
   * @description: 日期格式化 默认带时间  YYYY-MM-DD HH:mm:ss
   * @param {string} date
   * @param {string} format
   * @return {*}
   */
  const formatToDateTime = (
    date: string | number | Date | dayjs.Dayjs | null | undefined,
    format: string = DATE_TIME_FORMAT,
  ): string => {
    return date ? dayjs(date).format(format) : ''
  }

  /**
   * @description: 日期格式化 默认带时间  YYYY-MM-DD
   * @param {string} date
   * @param {string} format
   * @return {*}
   */
  const formatToDate = (
    date: string | number | Date | dayjs.Dayjs | null | undefined,
    format: string = DATE_FORMAT,
    isTimestamp: boolean = false,
  ): string => {
    if (isTimestamp)
      date = Number(date)
    return dayjs(date).isValid() ? dayjs(date).format(format) : '--'
  }

  /**
   * @description: 格式化文件大小
   * @param {number} bytes
   * @param {number} decimalPoint
   * @return {*}
   */
  const formatFileSize = (bytes: number, decimalPoint = 2): string => {
    if (bytes === 0)
      return '0 Bytes'
    const k = 1000
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    const str = Number.parseFloat((bytes / k ** i).toFixed(decimalPoint))
    return `${str} ${sizes[i]}`
  }

  /**
   * @description: 格式化返回数据
   * @return {*}
   */
  const formatData = <T = any>(response: HttpResponse<T>) => {
    const { code, data } = response
    if (code === '200') {
      return data
    }
    return []
  }

  /**
   * @description: 格式化金额数据
   * @param {number} val
   * @param format
   * @return {string}
   */
  const formatAmount = (val: number | string, format = '0,0.00'): string => {
    return val ? numeral(val).format(format) : format
  }

  /**
   * @description 格式化期间
   * @param val
   */
  const formatPeriod = (val: number | string, formatType: ILabelFormatType = 'period') => {
    if (!val)
      return ''

    if (typeof val === 'number')
      val = val.toString()

    if (typeof val === 'string')
      val = val.replace(/[-.]/g, '')

    const year = val.slice(0, 4)
    const month = val.slice(4, 6)

    const formatTypeMap: Record<ILabelFormatType, string> = {
      period: t('期'),
      month: t('月'),
    }
    // 年份
    const Y = `${year}`
    // 月份

    // 是否是期间字段
    const isPeriod = formatType === 'period'
    // 期 or 月

    if (isPeriod) {
      return `${year}${t('年')}${month}${formatTypeMap[formatType]}`
    }

    return ` ${month}/${Y}`
  }

  return {
    formatAmountOfPlace,
    formatPlaceOfAmount,
    formatBankAccount,
    formatFormRole,
    formatToDate,
    formatToDateTime,
    formatFileSize,
    formatData,
    formatAmount,
    formatPeriod,
  }
}
